import React, { useCallback, useEffect, useState } from "react";
import { useSwipeable } from "react-swipeable";

// Fullscreen image viewer for chat attachments. Opens when the user clicks an
// image inside a message and, if that message contains more than one image,
// lets the user step through the siblings via:
//   - left/right arrow buttons
//   - left/right keyboard keys
//   - swipe gesture (touch + click-drag, courtesy of react-swipeable)
// Single-image messages still open here so the click-to-zoom UX is consistent;
// the nav controls just hide themselves when there's nothing to navigate.
//
// Per-image actions (Reply, Delete) live in the toolbar at the top-right so
// the user can act on a single image after zooming in — distinct from the
// album-level actions in the message bubble's ⋮ dropdown. `messages` is a
// parallel-to-`images` array of the underlying server records, so the
// toolbar can fan its callbacks out to the right `message_id`.
const ImageLightbox = ({
  images = [],
  messages = [],
  startIndex = 0,
  currentUserId,
  onClose,
  onReply,
  onDelete,
}) => {
  const [index, setIndex] = useState(startIndex);

  // Re-sync the active index whenever the caller opens the viewer at a new
  // starting position. Without this, opening image #3 right after closing on
  // image #1 would still render #1 because the component instance was reused.
  useEffect(() => {
    setIndex(startIndex);
  }, [startIndex, images]);

  const total = images.length;
  const hasMultiple = total > 1;

  const goPrev = useCallback(() => {
    if (!hasMultiple) return;
    setIndex((current) => (current - 1 + total) % total);
  }, [hasMultiple, total]);

  const goNext = useCallback(() => {
    if (!hasMultiple) return;
    setIndex((current) => (current + 1) % total);
  }, [hasMultiple, total]);

  // Keyboard navigation + body-scroll lock while the viewer is up. Lock is
  // restored on unmount so we never leave the page un-scrollable.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose && onClose();
      } else if (e.key === "ArrowLeft") {
        goPrev();
      } else if (e.key === "ArrowRight") {
        goNext();
      }
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [goPrev, goNext, onClose]);

  // trackMouse:true so desktop users can click-drag to navigate, matching the
  // touch-swipe UX. preventScrollOnSwipe stops the browser from scrolling the
  // page while the user is in the middle of a horizontal swipe.
  const swipeHandlers = useSwipeable({
    onSwipedLeft: goNext,
    onSwipedRight: goPrev,
    preventScrollOnSwipe: true,
    trackMouse: true,
    delta: 30,
  });

  if (!total) return null;

  const currentSrc = images[index];
  const currentMessage = messages[index];

  // Per-image action gating:
  //   * Reply: surfaced whenever the parent supplied a handler — replying to
  //     someone else's image is a normal flow.
  //   * Delete: needs three things to all hold:
  //       (a) parent supplied an onDelete handler;
  //       (b) the current image's owner is the logged-in user (loose
  //           equality, same reasoning as MessageList: Sender_Id arrives
  //           typed inconsistently across socket events);
  //       (c) deleting that owner record would remove ONLY this image.
  //
  //     Condition (c) is the new one. Some sends arrive from the server as a
  //     SINGLE message record carrying `files: [N URLs]` — every gallery slot
  //     for that send shares one `message_id`, and the only delete API we
  //     have (`is_deleted`) operates per-record. Calling it would wipe all N
  //     images, defeating the per-image promise. We detect that case by
  //     counting how many gallery slots reference the SAME message object as
  //     the current slot — > 1 means a record owns multiple slots and we
  //     hide the trash icon. The bubble-level ⋮ Delete is still available
  //     for "remove the whole album" intent.
  const canReply = typeof onReply === "function" && !!currentMessage;
  // eslint-disable-next-line eqeqeq
  const isOwnMessage =
    !!currentMessage && currentUserId != null &&
    // eslint-disable-next-line eqeqeq
    currentMessage.Sender_Id == currentUserId;

  let ownerSlotCount = 0;
  if (currentMessage) {
    for (const m of messages) {
      if (m === currentMessage) {
        ownerSlotCount += 1;
        if (ownerSlotCount > 1) break;
      }
    }
  }
  const ownerHasOtherSlots = ownerSlotCount > 1;

  const canDelete =
    typeof onDelete === "function" && isOwnMessage && !ownerHasOtherSlots;

  // Backdrop click closes; clicks on the image / arrows / counter must NOT
  // bubble up and trigger close, so each one stops propagation.
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose && onClose();
  };

  return (
    <div
      className="comm-image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <div
        className="comm-image-lightbox__toolbar"
        onClick={(e) => e.stopPropagation()}
      >
        {canReply && (
          <button
            type="button"
            className="comm-image-lightbox__action"
            aria-label="Reply to this image"
            onClick={() => onReply(currentMessage, index)}
          >
            <i className="pi pi-reply" aria-hidden="true" />
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            className="comm-image-lightbox__action comm-image-lightbox__action--danger"
            aria-label="Delete this image"
            onClick={() => onDelete(currentMessage, index)}
          >
            <i className="pi pi-trash" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className="comm-image-lightbox__close"
          aria-label="Close image viewer"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      {hasMultiple && (
        <button
          type="button"
          className="comm-image-lightbox__nav comm-image-lightbox__nav--prev"
          aria-label="Previous image"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
        >
          ‹
        </button>
      )}

      <div
        className="comm-image-lightbox__stage"
        {...swipeHandlers}
        onClick={handleBackdropClick}
      >
        <img
          key={currentSrc}
          src={currentSrc}
          alt=""
          className="comm-image-lightbox__image"
          draggable={false}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {hasMultiple && (
        <button
          type="button"
          className="comm-image-lightbox__nav comm-image-lightbox__nav--next"
          aria-label="Next image"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
        >
          ›
        </button>
      )}

      {hasMultiple && (
        <div className="comm-image-lightbox__counter" aria-live="polite">
          {index + 1} / {total}
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;
